import PageLayout from '../../common/components/PageLayout/PageLayout'
import './HumanPractices.css'
import Title from '../../common/components/Title/Title'
import Accordion from '../../common/components/Accordion/Accordion'
import TextSeparator from '../../common/components/TextSeparator/TextSeparator'
import TableOfContent, {
  Parent
} from '../../common/components/TableOfContents/TableOfContent'
import MarginDecoration from '../../common/components/MarginDecoration/MarginDecoration'

function HumanPractices () {
  const toContent: Parent[] = [
    {
      displayName: 'An Uncharted Territory',
      id: 'uncharted-territory',
      children: []
    },
    {
      displayName: 'Gathering Knowledge',
      id: 'gathering-knowledge',
      children: [
        {
          displayName: 'Relevance and ethics',
          id: 'knowledge-1'
        },
        {
          displayName: 'Rhesus protein system',
          id: 'knowledge-2'
        },
        {
          displayName: 'Protein structure and complex prediction',
          id: 'knowledge-3'
        },
        {
          displayName: 'Fluidic dynamics and system optimization',
          id: 'knowledge-4'
        }
      ]
    },
    {
      displayName: 'In summary',
      id: 'in-summary',
      children: []
    }
  ]
  return (
    <PageLayout title="Human Practices">
      <MarginDecoration />
      <div className="human-practices-top">
        <div className="human-practices-toc-container">
          <div className="human-practices-toc-flex">
            <p>
              <div className="human-practices-toc">
                <TableOfContent content={toContent} />
              </div>
              In the face of an escalating health crisis and the emergence of
              new diseases, the demand for blood donations has surged, placing a
              tremendous strain on blood banks worldwide. It is within this
              challenging backdrop that our project,{' '}
              <b>Blood Cell Barber Shop</b>, was born.{' '}
              <b>Blood Cell Barber Shop</b> is a synthetic biology initiative to
              aid the global population. This year&apos;s project objective is
              to design an enzymatic antigen filtration device for blood cell
              samples, specifically targeting the removal of A and B antigens
              from blood donation samples. Across the globe, the need for safe
              blood is ever-present, with O-type blood being particularly
              precious due to its universal compatibility. To achieve our
              mission, we draw upon the insights gained from various studies and
              the pioneering work of previous iGEM teams, such as iGEM Taipei in
              2019.{' '}
            </p>
            <div className="human-practices-toc2">
              <TableOfContent content={toContent} />
            </div>
          </div>
        </div>
      </div>
      <TextSeparator />
      <Title title="An uncharted territory" id="uncharted-territory" isMid />
      <p>
        The team embarked on this project with a plethora of questions. To
        effectively navigate our project&apos;s intricacies, we diligently
        delved into each and every one of these inquiries. Our initial focus was
        on gaining a deep understanding of the ethical considerations and
        established norms pertaining to blood donations. We proactively sought
        insights from industry leaders and experts to ensure that our
        project&apos;s relevance and alignment with ethical standards were
        well-established. Additionally, we encountered technical challenges that
        required demanded the guidance of seasoned experts in the field. By
        rigorously addressing these ethical, pertinence, and technical
        questions, we fortified the foundation upon which our Blood Cell Barber
        Shop project stands.
      </p>
      <TextSeparator />
      <Title title="Gathering knowledge" id="gathering-knowledge" />
      <Title title="Relevance and ethics" id="knowledge-1" isSub />
      <p>
        At the beginning of the year, with our project freshly decided, we set
        out to find out more about this field from Héma-Québec, the local
        nonprofit organization in charge of blood donations and the distribution
        of blood products here in the province of Québec.
      </p>
      <p>
        First, we wanted to know more about the need for such a project.
        Héma-Québec informed us that enzymatic removal of antigens was tried
        several years ago with poor results. The reasons were the low efficiency
        of the enzymes used, the need for a matrix, and the requirement of
        cofactors in the hydrolysis reaction. They did however confirm that
        there is indeed a need for a supplementary supply of O- type blood in
        high-demand periods such as summer and winter. Furthermore, this
        accentuates the need for personalised blood transfusion, especially, for
        people with recurrent need of transfusion.
      </p>
      <p>
        Knowing the extensive variety of different antigen groups, they informed
        us that it would be wiser to tackle specifically ABO type and Rh type
        antigens, as they are by far the most immunogenic. These groups are
        followed by Kell type antigens, which are only detected in roughly 2% of
        the population. Additionally, they warned us that some glycohydrolases
        could create unusual epitopes after enzymatic degradation that could
        lead to some immune response. Moreover, they advised us to keep the
        working condition of our treatment close to physiological conditions (pH
        7,40 ± 0,05) and if possible at a temperature near 4 °C.
      </p>
      <Accordion title="Integration into our project">
        <p>
          In consideration of the past trials on enzymatic removal of antigens,
          we set out to identify and use more efficient enzymes. Previous
          degradation of A antigen (review; Taipei 2021) relying on the
          α-N-acetylgalactosaminidase from <i>Elizabethkingia meningoseptica</i>{' '}
          suffered from the low efficiency of the enzyme, the need for a dextran
          matrix to bring closer the enzyme to the blood cells, and NADH for the
          reaction turnover. In that regard, we found that a bipartite
          degradation of A antigen using enzymes from{' '}
          <i>Flavonifractor plautii</i> showed higher efficiency of antigen
          removal and, most particularly, without the need of a matrix and
          cofactors. As for degradation of B antigen, we decided to use enzymes
          from <i>Bacteroides fragilis</i>
        </p>

        <p>
          Adding the enzymes directly in blood procured from donations was also
          one of their main concerns. They recommended that we find a way to
          ensure that the final blood product would be free of our enzymes.
          Instead of adding our enzymes to blood donation bags, we thought of
          making the blood pass through a tubular system containing the fixated
          enzymes on the inner layer. The resulting blood product would be free
          of any enzymes and ABO-type antigens. We thus redirected our efforts
          into instead developing a fluidic system with our enzymes coating the
          inner layer of the system.
        </p>

        <p>
          Additionally, Héma-Québec also informed us that the targeting of the
          Rh antigen was a main focus for this type of research on blood
          donations, convincing us to work on both the ABO and the Rh systems.
          We were reluctant at first to work with Rh, as little information in
          regard to its structure was available and its immunogenicity does not
          arise from post-translational glycosylation like for the ABO system.
          We considered directed mutagenesis in somatic cells or even capping to
          remove or hide the recognised residues.
        </p>

        <p>
          This meeting with our local experts, Héma-Québec, caused us to change
          the primary focus of the project in favor of the development of an
          innovative fluidic system geared toward the enzymatic removal of
          antigens.
        </p>
      </Accordion>
      <TextSeparator />

      <Title title="Rhesus protein system" id="knowledge-2" isSub />
      <p>
        With our new design for the project based on the comments we got from
        Héma-Québec, we reached out to a professor that acted as a Principal
        Investigator for the team up to 2022: <u>Steve Charette</u>. We
        discussed with him our new strategy of degrading the specific
        glycosylation corresponding respectively to A and B antigens and our
        second strategy of hiding the Rh antigens via mutagenesis or capping.
      </p>

      <p>
        On the subject of Rh antigens, he informed us that he worked in the past
        on Rh-like proteins in amoeba. Those proteins were involved with the
        contractile vacuole. Out of curiosity, he looked at the similarity of
        sequence and the phylogeny between the amoeba Rh-like proteins and the
        human RhD protein isoform 1, which incited us to integrate the human Rh
        homologues. We then discussed with him the results, which briefly showed
        RhAG is more closely related to RhBG and RhCG which show a similarity to
        amoeba Rh-like proteins and that RhD and RhCE are highly identical.
        These results are consistent with previous reports suggesting they arise
        from genome duplication. Consequently, he recommended that we expand the
        phylogeny analyses to other Rh-like proteins, as it would be interesting
        to follow its evolution.
      </p>
      <Accordion title="Integration into our project">
        <p>
          Surprisingly, the discussions we had with Steve Charette with the
          purpose of getting a second opinion on our project led to the addition
          of significant content to it. It brought us to investigate the
          relation of known Rh-like proteins across different species to better
          understand the evolution of such proteins. The preliminary phylogeny
          we made based on those discussions led to identification of important
          similarities of different Rh-like proteins that we would later use for
          structural modelling. Furthermore, we followed his recommendation of
          expanding our phylogeny to other and more distanced Rh-like proteins.
          In that regard, it is currently ongoing.
        </p>

        <p>
          This overall added a new dimension in our project to expand the
          current knowledge on Rh-like proteins.
        </p>
      </Accordion>
      <TextSeparator />

      <Title
        title="Protein structure and complex prediction"
        id="knowledge-3"
        isSub
      />
      <p>
        Our meetings with the <u>experts from Héma-Québec</u> established the
        need for a lot of work to be done on the Rh protein complex and
        potential methods to treat it for blood transfusions. After reviewing
        available literature on the subject, we determined that the RhD subunit
        was the most immunogenic, and therefore the best candidate for eventual
        mutations or protein capping. We started to look for the published 3D
        structures of RhD, with and without its surrounding protein complex. To
        our surprise,{' '}
        <b>
          there was no published tri-dimensional structure of RhD, alone or in a
          complex.
        </b>
        We decided to determine that structure using bioinformatics methods.
      </p>
      <p>
        The first expert we consulted on this subject was <u>Stéphane Gagné</u>,
        a local researcher with great expertise in protein structure prediction.
        He helped us determine what had already been done in terms of modeling
        and assembly of the Rh protein complex. We asked him what would be the
        best strategy to model the different Rh proteins and its complex, as
        well as advice on specific bioinformatics tools to use. He suggested a{' '}
        <i>de novo</i> approach with elements of protein homology. Additionally,
        he informed us on common quality control measures and working
        parameters, which we integrated into our structure prediction methods.
        We also discussed with him our possible strategy to hide our substitute
        immunogen segments via directed mutagenesis
      </p>

      <p>
        The second expert we consulted was <u>Patrick Lagüe</u>, a local
        researcher with extensive expertise in molecular dynamics, with a
        specialization in proteins and membranes. We presented him the results
        of our protein structure predictions so he could advise us on the
        subsequent steps to take. He confirmed that our predictions were high
        quality. We consulted him on ways to obtain information about the
        stability of the predicted protein complexes. He told us to contact
        François Rouleau to estimate their folding energy using FoldX. He
        suggested we perform molecular dynamics for the complexes with the best
        folding energy and most interesting biological implications.
      </p>

      <p>
        <u>François Rouleau</u> is a PhD candidate and former iGEM member who
        was recommended to us for advice on the use of FoldX and ways to compute
        subunit affinities within the Rh complex. He helped us determine which
        functions to use within the FoldX suite, how to perform predictions that
        reflect biological reality, and how to evaluate the results’ quality. He
        also provided additional confirmation of the high quality of the protein
        models produced until that point.
      </p>

      <Accordion title="Integration into our project">
        <p>
          Our discussions with Stéphane Gagné helped develop our <i>de novo</i>{' '}
          approach. We used a combination of Blast, varied secondary structure
          prediction tools and AlphaFold2 Multimer. Those recommendations led us
          to the 3-dimensional simulation of each monomer, dimer, trimer and
          specific tetramer combinations of the human Rh complex. Such
          simulations represent a significant contribution to our understanding
          of the Rh complex for further analysis, but also they confer
          significant knowledge advance in the field.
        </p>
        <p>
          The FoldX calculations, guided by François Rouleau and Patrick Lagüe,
          determined which protein complex predictions should be used for
          subsequent steps and any implementation. It also confirmed our
          previous assumptions from the complex structure prediction step, that
          certain combinations were much more unstable and biologically
          improbable. This allowed us to then identify the most probable
          biological conformation of the Rh complex, 3RhAG, 2RhAG-1RhCE and
          2RhAG-1RhD, for further investigation of immunogenicity of exposed
          segments.
        </p>

        <p>
          The molecular dynamics were then performed using Charmm-gui, using a
          protocol suggested to us and modified by Patrick Lagüe. His advice was
          most helpful in designing the asymmetrical lipid membrane within the
          constraints of Charmm-gui and the OPM-PPM3 servers. This enabled us to
          visualise the aforementioned exposed segments of the Rh complex when
          integrated in a human plasma membrane. Although the current version of
          this modelling is still in its preliminary form, we are currently
          improving the modelling based on their recommendations.
        </p>
      </Accordion>
      <TextSeparator />

      <Title
        title="Fluidic dynamics and system optimization"
        id="knowledge-4"
        isSub
      />
      <p>
        In order to better implement our project, we thought that having a
        modular system with fixed enzymes would enable a higher enzymatic
        efficiency. For that purpose, we reached out to{' '}
        <u>Hossein Hassanzadeh</u>, whom we had collaborated with in past iGEM
        projects.
      </p>

      <p>
        We discussed with him our implementation, as he is an expert in fluidic
        systems. Although such systems would be quite a challenge to simulate,
        it would be intriguing to investigate and could lead to more concrete
        therapeutic avenues. So we set out to identify the different parameters
        to work with, in order to obtain a preliminary model. Considering the
        sheer amount of parameters and combinations, we concluded with Hossein
        that a <i>Design of experiments</i> combined with multiple fluid
        dynamics simulations could help us identify optimal parameters. In that
        regard, with the help of Hossein who generated the different model
        geometry projects, we simulated the flow of blood in different systems.
      </p>

      <Accordion title="Integration into our project">
        <p>
          This helped to concretise our idea of implementing our enzymes into a
          fluidic system for blood antigen enzymatic removal. Hossein had a huge
          impact on defining the most relevant parameters to consider as
          variables, leaving the other parameters as constant depending on the
          selected variables.
        </p>

        <p>
          In regard to the models, we simplified the simulation to 2-dimensional
          models in accordance with his suggestions to speed up the computation
          and to preliminarily identify the configuration area, leading to
          better results.
        </p>

        <p>
          Additionally, as we wanted to investigate the efficiency of our
          different enzymes, Hossein suggested that simulation of enzymatic
          reaction could be coupled to the fluid dynamics simulation. Such
          analysis is still ongoing, as such simulation involves large
          computation resources.
        </p>
      </Accordion>
      <TextSeparator />

      <Title title="In summary" id="in-summary" isMid />

      <p>
        In consideration of the need for universal and personalised blood
        transfusion, we designed our project to respond to such needs according
        to the recommendation of experts. Our project has the potential to
        relieve the disadvantages of past trials on enzymatic removal of
        ABO-type antigens and to expand the knowledge on human Rh complex
        structure.
      </p>

      <p>
        As a result of the implication and suggestion of experts in their
        respective fields, we reshaped our project from the conceptualisation to
        specific modelling steps, to better fit the needs and to optimise our
        system for a realistic and efficient use in the clinical field.
      </p>
    </PageLayout>
  )
}

export default HumanPractices
